import type { BoxProps} from '@chakra-ui/react'
import { Box, keyframes, Spinner } from '@chakra-ui/react'
import { createContext, useContext, useState } from 'react'

const scale = keyframes`
0% { 
  transform: scale(1); 
}
50% { 
  transform: scale(1.05); 
}
100% { 
  transform: scale(1); 
}
`

interface LoadingOverlayProps extends BoxProps {
  children: React.ReactNode
  isLoading?: boolean
}

interface ILoadingOverlayContext {
  loading: boolean,
  setLoading: (loading: boolean) => void,
  isAnimating: boolean,
  startAnimation: () => void
}

const LoadingOverlayContext = createContext<ILoadingOverlayContext>({
  loading: false,
  setLoading: () => { },
  isAnimating: false,
  startAnimation: () => { }
})

export const LoadingOverlay = ({ children, isLoading = false, ...rest }: LoadingOverlayProps) => {
  const [loading, setLoading] = useState(isLoading)
  const [isAnimating, setIsAnimating] = useState(false)

  // spusti pulse animaci
  const startAnimation = () => {
    setIsAnimating(true)
    setTimeout(() => setIsAnimating(false), 1000) // 2*0.5s viz animation property nize
  }

  return (
    <LoadingOverlayContext.Provider value={
      {
        loading: loading,
        setLoading: setLoading,
        isAnimating: isAnimating,
        startAnimation: startAnimation
      }
    }>
      <Box
        sx={{ position: 'relative' }}
        animation={isAnimating ? `${scale} 0.5s ease-in-out 2` : undefined}
        {...rest}
      >
        <Box sx={{ position: 'relative' }}>{children}</Box>

        {loading && (
          <Box
            sx={{
              position: 'absolute',
              bg: 'rgba(255, 255, 255, 0.5)',
              cursor: 'progress',
              inset: 0,
              zIndex: 'docked',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <Spinner />
          </Box>
        )}
      </Box>
    </LoadingOverlayContext.Provider>
  )
}

export const useLoadingOverlay = () => {
  const context = useContext(LoadingOverlayContext)

  if (context === undefined || Object.keys(context).length == 0) {
    throw new Error('useLoadingOverlay must be used within a LoadingOverlay!')
  }

  return context
}